import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Store, select } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { AppState } from '../../../../app.reducers';
import { Observable, Subject, combineLatest } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { getSelectedProperty, getSelectedFiscalYear, getPropertyTypes, getPropertyReserveStudies, getSavePropertyDetailsError } from '../../property-store/property.selectors';
import { LoadPropertyRequest, Property, PropertyReserveStudy, PropertyType } from '../../shared/models';
import { ToastrService } from 'ngx-toastr';
import { AddPropertyReserveStudyAction, ClearSavePropertyDetailsErrorAction, DeletePropertyReserveStudyAction, GetAllPropertyReserveStudyAction, LoadSelectedPropertyAction, RemovePropertyProfileImageAction, SavePropertyDetailsAction, UpdatePropertyReserveStudyAction } from '../../property-store/property.actions';
import { propertyActionTypes } from '../../property-store/property.actions';
import { UserProfile } from '../../../user/models';
import { getUserProfile } from '../../../user/redux/user.selectors';
import { getUserRole } from 'app/shared/auth/redux/auth.selectors';
import { DxTabPanelComponent } from 'devextreme-angular';
import { TerminologyDbDisplayService } from 'app/internal/shared/services/terminology-db-display.service';

@Component({
  templateUrl: './property-details.component.html',
  styleUrls: ['./property-details.component.scss'],
  standalone: false
})
export class PropertyDetailsComponent implements OnInit, OnDestroy, AfterViewInit {

  @ViewChild('propertyTabPanel', { static: false }) propertyTabPanel: DxTabPanelComponent;

  propertyTypes$: Observable<PropertyType[]>;
  property$: Observable<Property>;
  userProfile$: Observable<UserProfile>;
  userRole$: Observable<string>;
  reserveStudies$: Observable<PropertyReserveStudy[]>;
  savePropertyDetailsError$: Observable<string | null>;
  showActivityTab$: Observable<boolean>;

  feedbackMessages: any[] = [];
  activeTab = 'general';
  selectedIndex = 0;

  showFinancialsTab = false;
  showReserveStudyTab = false;
  showConnectedAppsTab = false;
  showActivityTab = false;
  reserveStudyTabTitle = 'Reserve Study History';

  private activityTabEnabledByFiscalYear = false;
  private property: Property;
  private userRole: string;
  private destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private store: Store<AppState>,
    private toastr: ToastrService,
    private actions$: Actions,
    private terminologyDb: TerminologyDbDisplayService,
    private cdr: ChangeDetectorRef,
  ) {

    this.property$ = this.store.pipe(select(getSelectedProperty));
    this.propertyTypes$ = this.store.pipe(select(getPropertyTypes));
    this.userProfile$ = this.store.pipe(select(getUserProfile));
    this.userRole$ = this.store.pipe(select(getUserRole));
    this.reserveStudies$ = this.store.pipe(select(getPropertyReserveStudies));
    this.savePropertyDetailsError$ = this.store.pipe(select(getSavePropertyDetailsError));
    this.showActivityTab$ = this.store.pipe(
      select(getSelectedFiscalYear),
      map(fy => !!fy && (fy.isCurrentFiscalYear || fy.isFutureFiscalYear)),
    );
  }

  ngOnInit(): void {
    this.store.dispatch(new GetAllPropertyReserveStudyAction(null));

    this.subsToTabVisibility();

    this.actions$.pipe(
      ofType(propertyActionTypes.SAVE_PROPERTY_DETAILS_SUCCESS),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.toastr.success('Property Details Saved');
      this.router.navigateByUrl('/useraccount/properties/dashboard');
    });

    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
      if (params['tab']) {
        this.activeTab = params['tab'];
        this.applyActiveTabIndex();
      }
    });

    this.terminologyDb.refreshOnClubChanges(this.cdr, this.destroy$);
    this.updateReserveStudyTabTitle();
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.applyActiveTabIndex(), 0);
  }

  ngOnDestroy(): void {
    this.store.dispatch(new ClearSavePropertyDetailsErrorAction());
    this.destroy$.next();
    this.destroy$.complete();
  }

  onTabPanelInitialized(): void {
    setTimeout(() => this.applyActiveTabIndex(), 0);
  }

  onTabPanelSelectionChanged(event: { component: { option: (name: string) => number } }) {
    const idx = event.component.option('selectedIndex');
    const tabId = this.getVisibleTabIds()[idx];
    if (tabId) {
      this.activeTab = tabId;
      this.selectedIndex = idx;
    }
  }

  onCancel() {
    this.router.navigate(['useraccount/properties/dashboard']);
  }

  onSave(property: Property) {
    this.store.dispatch(new SavePropertyDetailsAction({ property: property }));
  }

  onSaveImage(propertyId: string) {
    this.store.dispatch(new LoadSelectedPropertyAction(new LoadPropertyRequest(propertyId)));
    this.toastr.success('Profile Image Saved');
  }

  onRemoveImage(propertyId: string) {
    this.store.dispatch(new RemovePropertyProfileImageAction(propertyId));
    this.toastr.success('Profile Image Removed');
  }

  onAddReserveStudy(propertyReserveStudy: PropertyReserveStudy) {
    this.store.dispatch(new AddPropertyReserveStudyAction(propertyReserveStudy));
  }

  onUpdateReserveStudy(propertyReserveStudy: PropertyReserveStudy) {
    this.store.dispatch(new UpdatePropertyReserveStudyAction(propertyReserveStudy));
  }

  onDeleteReserveStudy(propertyReserveStudy: PropertyReserveStudy) {
    this.store.dispatch(new DeletePropertyReserveStudyAction(
      { propertyId: propertyReserveStudy.propertyId, propertyReserveStudyId: propertyReserveStudy.propertyReserveStudyId }
    ));
  }

  private subsToTabVisibility(): void {
    combineLatest([this.property$, this.userRole$, this.showActivityTab$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([property, userRole, showActivity]) => {
        this.property = property;
        this.userRole = userRole;
        this.activityTabEnabledByFiscalYear = showActivity;
        this.updateTabVisibility();
        this.applyActiveTabIndex();
        this.cdr.markForCheck();
      });
  }

  private updateTabVisibility(): void {
    this.showFinancialsTab = this.hasRole('Admin', 'Manager', 'PortfolioAdmin');
    this.showReserveStudyTab = this.hasRole('Admin', 'Manager', 'Member', 'PortfolioAdmin');
    this.showConnectedAppsTab = !!this.property?.isIntegratedProperty
      && this.hasRole('Admin', 'Manager', 'PortfolioAdmin');
    this.showActivityTab = this.activityTabEnabledByFiscalYear
      && this.hasRole('Admin', 'Manager', 'PortfolioAdmin');
  }

  private updateReserveStudyTabTitle(): void {
    const label = this.terminologyDb.displayDbName('Reserve Study') || 'Reserve Study';
    this.reserveStudyTabTitle = `${label} History`;
  }

  private hasRole(...roles: string[]): boolean {
    return !!this.userRole && roles.includes(this.userRole);
  }

  private getVisibleTabIds(): string[] {
    const ids = ['general'];
    if (this.showFinancialsTab) {
      ids.push('financials');
    }
    if (this.showReserveStudyTab) {
      ids.push('reserve-study');
    }
    if (this.showConnectedAppsTab) {
      ids.push('connected-apps');
    }
    if (this.showActivityTab) {
      ids.push('activity');
    }
    return ids;
  }

  private applyActiveTabIndex(): void {
    const ids = this.getVisibleTabIds();
    const idx = ids.indexOf(this.activeTab);
    this.selectedIndex = idx >= 0 ? idx : 0;
    if (this.propertyTabPanel) {
      this.propertyTabPanel.selectedIndex = this.selectedIndex;
    }
    this.cdr.markForCheck();
  }
}
